// Canonical CRM opportunity custom-field payload.
// Was inlined verbatim 3× in LeadBuyerMatches.js (GHL / FUB / Salesforce blocks);
// this is the one source of truth so the three providers can never drift.

import { prioritizePropertyPhotos, upgradeRdcpixUrl } from './imageUtils';

// Image-folder providers we filter out because they are not directly downloadable.
const FOLDER_HOSTS = [
    'photos.google.com',
    'photos.app.goo.gl',
    'drive.google.com',
    'dropbox.com',
    'onedrive.live.com',
    '1drv.ms',
    'sharepoint.com',
];

function isDownloadableUrl(u) {
    if (!u || typeof u !== 'string') return false;
    return !FOLDER_HOSTS.some((host) => u.includes(host));
}

function pickFeaturedImage(lead) {
    const photos = lead?.photoUrls || {};
    // Never pick a photo the operator deleted (PhotoManagerModal moves deleted
    // URLs to excludedImageUrls without rewriting imageUrls, and the gallery
    // hides them — so an unfiltered pick would send buyers a photo the operator
    // can't even see anymore).
    const excluded = new Set((lead?.excludedImageUrls || []).map(upgradeRdcpixUrl));
    // Upgrade Realtor thumbnails before deduping: repairCostImageUrls can hold the
    // full-size URL while imageUrls still holds the raw 120x80 one, and whichever
    // lands first becomes the {{featured_image}} we send to the CRM and to buyers.
    const all = [
        ...(lead?.repairCostImageUrls || []),
        ...((lead?.imageUrls) || []).filter(isDownloadableUrl),
        ...[photos.zillow, photos.redfin, photos.realtor].filter((u) => u && typeof u === 'string' && u.trim().length > 0),
    ].map(upgradeRdcpixUrl).filter((u, i, arr) => u && !excluded.has(u) && arr.indexOf(u) === i);

    // Real property photos (folder/uploaded/listing) win over street-view/map
    // imagery, matching the lead card + deal page. photoOrder still decides
    // among real photos; only supplementary street views are demoted.
    let ordered = all;
    let hasExplicitOrder = false;
    if (Array.isArray(lead?.photoOrder) && lead.photoOrder.length > 0) {
        const inAll = new Set(all);
        const front = lead.photoOrder.map(upgradeRdcpixUrl).filter((u) => inAll.has(u));
        const frontSet = new Set(front);
        ordered = [...front, ...all.filter((u) => !frontSet.has(u))];
        hasExplicitOrder = front.length > 0;
    }
    // respectOrder so the operator's chosen featured photo wins as-is, matching
    // the lead card + detail page; otherwise lead with the backend-chosen
    // front-exterior shot, falling back to the folder-first sort.
    return prioritizePropertyPhotos(ordered, { respectOrder: hasExplicitOrder, featuredUrl: upgradeRdcpixUrl(lead?.featuredImageUrl) })[0] || null;
}

// Simple per-lead photo pick: first downloadable, non-excluded imageUrls entry,
// then listing-provider photos. Deliberately NOT pickFeaturedImage's
// photoOrder/featured logic — it mirrors the backend picker
// (functions/mcpAPI/crmSendPayload.js pickFeaturedImage, KEEP IN SYNC) so app
// and MCP sends stamp identical values.
//
// ⚠️ PACKAGES ONLY. A SINGLE lead's opportunity image is no longer picked by
// either simple picker on either side: both paths now use the page's gallery
// hero (deriveDisplayImageUrls[0]) — passed in as `displayImageUrl` here, and
// re-derived by functions/utils/displayPhotos.js for MCP / auto-send. This
// heuristic could not see repairCostImageUrls or featuredImageUrl and did no
// street-view demotion, which sent 40 buyers a street view of 6431 Rock Canyon
// Trl. Do not route a single lead back through it.
//
// The isDownloadableUrl filter matters:
// add_lead_image_link stores a Drive/Dropbox FOLDER url at imageUrls[0], which
// must never reach the CRM or a buyer-facing {{featured_image}} link.
function pickSimpleLeadImage(lead) {
    const excluded = new Set((lead?.excludedImageUrls || []).map(upgradeRdcpixUrl));
    const photos = lead?.photoUrls || {};
    // imageUrls is read FIRST and, for an on-market lead, holds the raw Realtor
    // gallery — i.e. 120x80 thumbnails. Upgrading here is what stops
    // {{featured_image}} stamping a postage stamp into the CRM and buyer emails.
    const candidates = [
        ...((lead?.imageUrls) || []).filter(isDownloadableUrl),
        photos.zillow, photos.redfin, photos.realtor,
    ].map(upgradeRdcpixUrl).filter((u) => u && typeof u === 'string' && u.trim().length > 0 && !excluded.has(u));
    return candidates[0] || null;
}

// The single photo that represents a PACKAGE: the first member with a usable
// photo (simple pick), falling through to later members so a package whose
// first member has only a folder link still gets a real photo from member 2..N.
function pickPortfolioImage(portfolioLeads) {
    for (const lead of (portfolioLeads || [])) {
        const image = pickSimpleLeadImage(lead);
        if (image) return image;
    }
    return null;
}

function conditionFromCpsf(repairCost, sqft) {
    const rc = Number(repairCost) || 0;
    const sf = Number(sqft) || 0;
    if (!rc || !sf) return null;
    const cpsf = rc / sf;
    if (cpsf <= 5)  return 'Turnkey';
    if (cpsf <= 15) return 'Cosmetic';
    if (cpsf <= 30) return 'Light Rehab';
    if (cpsf <= 60) return 'Value Add';
    return 'Gut Rehab';
}

// Build the customFieldValues object for an opportunity payload. The shared
// base shape is the FUB/Salesforce key set; GHL historically also receives
// four address keys (Street Address / City / State / Zip Code).
//
// SYNC NOTE: the GHL field NAMES produced here are mirrored in
// functions/flows/ghl/provisionOpportunityFields.js (GHL_OPPORTUNITY_FIELD_NAMES),
// which pre-creates them in the location on connect. functions/ can't import
// this file (separate package), so if you add/rename a GHL opportunity field
// here, update that array too or the new field won't be provisioned.
//
// One-way exceptions: "Clozers Message", "Clozers Subject", and "Clozers Buybox"
// are provisioned there but are NOT built here. The buyer message + email subject
// ride as top-level `dealMessage` / `dealSubject`; the per-buyer buy-box fit block
// rides as the top-level `buyBoxFitByBuyerId` map (+ `buyBoxEditLinkBase`).
// createGHLOpportunity ensures + stamps all three per buyer, NOT through
// customFieldValues. Do NOT add them here, or they would be sent twice.
//
// `provider` is required and controls whether the address keys are included.
// Defaulting it would risk silently re-introducing the FUB/SF regression
// the audit caught — explicit > implicit here.
//
// `coLivingReturns` is the co-living return model for this deal (the computeCoLivingReturns
// output — { hasData, cashFlowMonthly, cashOnCash }) or null/absent for non-co-living
// deals. Only the GHL branch reads it (populates "Cash Flow" + "Cash on Cash Return");
// pass it through from the send context. Absent ⇒ both fields null (blank in GHL),
// which is the correct state for flip/rental deals that have no such metric.
// `displayImageUrl` is the gallery's hero — deriveDisplayImageUrls(lead, displayLabels)[0],
// which the sending page has already computed for its own photo strip. Pass it and the CRM
// "Image" (and the {{featured_image}} token that mirrors it) is EXACTLY the photo the operator
// is looking at, including the classifier's labelled set, which pickFeaturedImage below cannot
// see. functions/utils/displayPhotos.js derives the same value for MCP / auto-send.
// Omitted ⇒ pickFeaturedImage, preserving today's behaviour for callers that have no gallery
// in scope (Deals / DealDetail / BuyerSendDeals / SelfPurchaseModal).
export function buildOpportunityCustomFields(lead, calculatorInputs, propertyId, provider, coLivingReturns, displayImageUrl = null) {
    if (!provider) {
        throw new Error('buildOpportunityCustomFields: provider is required');
    }

    const addr = lead?.address || {};
    const characteristics = lead?.verifiedCharacteristics || {};
    const featuredImage = (typeof displayImageUrl === 'string' && displayImageUrl) || pickFeaturedImage(lead);
    const occupancy = lead?.customFields?.tenantOccupied != null
        ? (lead.customFields.tenantOccupied ? 'Occupied' : 'Vacant')
        : null;

    const base = {
        Bedrooms: characteristics.bedrooms ?? null,
        Bathrooms: characteristics.bathrooms ?? null,
        'Square Footage': characteristics.livingSquareFeet ?? null,
        'Year Built': characteristics.yearBuilt ?? null,
        Price: Number(calculatorInputs?.purchasePrice) || null,
        Image: featuredImage,
        clozers_opportunity_id: lead?.leadId || propertyId || null,
        Occupancy: occupancy,
        Condition: conditionFromCpsf(calculatorInputs?.repairCost, characteristics.livingSquareFeet),
        ARV: Number(calculatorInputs?.arv) || null,
        'Repair Cost': Number(calculatorInputs?.repairCost) || null,
    };

    // GHL is the only provider that historically receives address fields, and the
    // only one provisioned with the co-living return fields.
    if (provider === 'ghl') {
        const cl = coLivingReturns || {};
        return {
            ...base,
            'Street Address': addr.streetAddress || null,
            City: addr.city || null,
            State: addr.state || null,
            'Zip Code': addr.zipcode || null,
            // Co-living return metrics — only co-living deals produce these (flip/rental
            // deals leave them null). Cash flow is levered monthly $ (rounded to the
            // dollar); cash-on-cash is an annual % (2dp), matching the deal-page ledger.
            'Cash Flow': cl.hasData && typeof cl.cashFlowMonthly === 'number' ? Math.round(cl.cashFlowMonthly) : null,
            'Cash on Cash Return': cl.hasData && typeof cl.cashOnCash === 'number' ? cl.cashOnCash : null,
        };
    }

    return base;
}

export { conditionFromCpsf, pickFeaturedImage, pickSimpleLeadImage, pickPortfolioImage };
